import { useQuery, useMutation } from '@tanstack/react-query'
import { I18nMessagesFilterParams, CreateI18nMessageParams, UpdateI18nMessageParams } from '@/types/i18n-message'
import { useI18nMessagesStore } from '@/store'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const useI18nMessages = (params: I18nMessagesFilterParams) => {
  const fetchI18nMessages = useI18nMessagesStore(state => state.fetchI18nMessages)

  return useQuery({
    queryKey: ['i18nMessages', params],
    queryFn: () => fetchI18nMessages(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  })
}

export const useI18nMessage = (messageId: string) => {
  const getI18nMessage = useI18nMessagesStore(state => state.getI18nMessage)

  return useQuery({
    queryKey: ['i18nMessage', messageId],
    queryFn: () => getI18nMessage(messageId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!messageId
  })
}

export const useCreateI18nMessage = () => {
  const createI18nMessage = useI18nMessagesStore(state => state.createI18nMessage)

  return useMutation({
    mutationFn: (params: CreateI18nMessageParams) => createI18nMessage(params),
    onSuccess: () => {},
    throwOnError: false,
    meta: {
      errorMessage: false
    }
  })
}

export const useUpdateI18nMessage = () => {
  const updateI18nMessage = useI18nMessagesStore(state => state.updateI18nMessage)

  return useMutation({
    mutationFn: ({ messageId, params }: { messageId: string; params: UpdateI18nMessageParams }) =>
      updateI18nMessage(messageId, params),
    onSuccess: () => {},
    throwOnError: false,
    meta: {
      errorMessage: false
    }
  })
}

export const useDeleteI18nMessage = () => {
  const deleteI18nMessage = useI18nMessagesStore(state => state.deleteI18nMessage)

  return useMutation({
    mutationFn: (messageId: string) => deleteI18nMessage(messageId),
    onSuccess: () => {},
    throwOnError: false
  })
}

export const useBulkDeleteI18nMessages = () => {
  const bulkDeleteI18nMessages = useI18nMessagesStore(state => state.bulkDeleteI18nMessages)
  const deselectAllMessages = useI18nMessagesStore(state => state.deselectAllMessages)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteI18nMessages(ids),
    onSuccess: response => {
      deselectAllMessages()
      handleBulkDeleteSuccess(response)
    }
  })
}
